import { useState, useCallback } from 'react';
import { GuestSide } from '../../../types/invitation';
import { rsvpService, seatingService } from '../../../lib/supabase';
import { collaborationService } from '../../../lib/collaborationService';
import { createLogger } from '../../../lib/logger';
import { RSVPResponseWithTable } from '../types';

const log = createLogger('rsvp');

interface UseGuestMutationsParams {
  inviteCode: string;
  userId: string | undefined;
  rsvpResponses: RSVPResponseWithTable[];
  onRefresh: () => Promise<void>;
  onDataChange?: () => void;
  showCollaborationAlert: (editor: string, action: string) => void;
  checkGuestLimit?: (increment: number) => boolean;
}

export function useGuestMutations({
  inviteCode,
  userId,
  rsvpResponses,
  onRefresh,
  onDataChange,
  showCollaborationAlert,
  checkGuestLimit,
}: UseGuestMutationsParams) {
  const [updatingGuestCount, setUpdatingGuestCount] = useState<string | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [updatingGuestName, setUpdatingGuestName] = useState<string | null>(null);
  const [deletingRSVPId, setDeletingRSVPId] = useState<string | null>(null);
  const [sendingNotificationId, setSendingNotificationId] = useState<string | null>(null);

  const checkGuestSeatingStatus = useCallback((rsvpId: string) => {
    const guestWithTable = rsvpResponses.find(r => r.id === rsvpId);
    return {
      hasSeating: !!guestWithTable?.tableId,
      tableName: guestWithTable?.tableName,
      tableId: guestWithTable?.tableId
    };
  }, [rsvpResponses]);

  const clearGuestFromSeating = useCallback(async (rsvpId: string) => {
    try {
      await seatingService.removeGuestFromSeating(inviteCode, rsvpId);
    } catch (error) {
      log.error('清理座位安排失敗:', error);
      throw new Error('清理座位安排失敗，但將繼續刪除 RSVP');
    }
  }, [inviteCode]);

  const handleUpdateGuestCount = useCallback(async (rsvpId: string, currentCount: number, increment: number) => {
    if (!userId) return;

    const newCount = currentCount + increment;
    if (newCount < 1) {
      alert('出席人數不能少於 1 人');
      return;
    }
    if (newCount > 10) {
      alert('出席人數不能超過 10 人');
      return;
    }

    if (increment > 0 && checkGuestLimit && !checkGuestLimit(increment)) {
      return;
    }

    setUpdatingGuestCount(rsvpId);

    try {
      const collaborationCheck = await collaborationService.checkRecentActivity(
        'rsvp_responses', rsvpId, userId
      );

      if (collaborationCheck.hasConflict) {
        showCollaborationAlert(
          collaborationCheck.lastEditor || '其他協作者',
          '修改了這位賓客的人數'
        );
      }

      await rsvpService.updateGuestCount(rsvpId, newCount, userId);

      await collaborationService.updateCollaborationMetadata(
        'rsvp_responses', rsvpId, userId, inviteCode
      );

      await onRefresh();

    } catch (error) {
      log.error('更新賓客人數失敗:', error);
      alert(error instanceof Error ? error.message : '更新失敗，請稍後再試');
    } finally {
      setUpdatingGuestCount(null);
    }
  }, [userId, inviteCode, onRefresh, showCollaborationAlert, checkGuestLimit]);

  const handleGuestSideToggle = useCallback(async (rsvpId: string, currentGuestSide: GuestSide) => {
    if (!userId) return;

    const newGuestSide: GuestSide = currentGuestSide === 'bride' ? 'groom' : 'bride';

    try {
      const collaborationCheck = await collaborationService.checkRecentActivity(
        'rsvp_responses', rsvpId, userId
      );

      if (collaborationCheck.hasConflict) {
        showCollaborationAlert(
          collaborationCheck.lastEditor || '其他協作者',
          '修改了這位賓客的親友分類'
        );
      }

      await rsvpService.updateGuestSide(rsvpId, newGuestSide, userId);

      await collaborationService.updateCollaborationMetadata(
        'rsvp_responses', rsvpId, userId, inviteCode
      );

      await onRefresh();

    } catch (error) {
      log.error('更新賓客親友分類失敗:', error);
      alert(error instanceof Error ? error.message : '更新失敗，請稍後再試');
    }
  }, [userId, inviteCode, onRefresh, showCollaborationAlert]);

  const handleStartEditName = useCallback((rsvpId: string, currentName: string) => {
    setEditingNameId(rsvpId);
    setEditingNameValue(currentName);
  }, []);

  const handleCancelEditName = useCallback(() => {
    setEditingNameId(null);
    setEditingNameValue('');
  }, []);

  const handleUpdateGuestName = useCallback(async (rsvpId: string) => {
    if (!userId) return;

    const trimmedName = editingNameValue.trim();
    if (!trimmedName) {
      alert('賓客姓名不能為空');
      return;
    }

    setUpdatingGuestName(rsvpId);

    try {
      const collaborationCheck = await collaborationService.checkRecentActivity(
        'rsvp_responses', rsvpId, userId
      );

      if (collaborationCheck.hasConflict) {
        showCollaborationAlert(
          collaborationCheck.lastEditor || '其他協作者',
          '修改了這位賓客的姓名'
        );
      }

      await rsvpService.updateGuestName(rsvpId, trimmedName, userId);

      await collaborationService.updateCollaborationMetadata(
        'rsvp_responses', rsvpId, userId, inviteCode
      );

      await onRefresh();
      handleCancelEditName();

    } catch (error) {
      log.error('更新賓客姓名失敗:', error);
      alert(error instanceof Error ? error.message : '更新失敗，請稍後再試');
    } finally {
      setUpdatingGuestName(null);
    }
  }, [userId, inviteCode, editingNameValue, onRefresh, showCollaborationAlert, handleCancelEditName]);

  const handleNameInputKeyDown = useCallback((e: React.KeyboardEvent, rsvpId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleUpdateGuestName(rsvpId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEditName();
    }
  }, [handleUpdateGuestName, handleCancelEditName]);

  const handleDeleteRSVP = useCallback(async (rsvpId: string, guestName: string) => {
    if (!userId) return;

    const seatingStatus = checkGuestSeatingStatus(rsvpId);

    let confirmMessage = `確定要刪除 ${guestName} 的 RSVP 回覆嗎？此操作無法撤銷。`;
    if (seatingStatus.hasSeating) {
      confirmMessage = `${guestName} 目前安排在「${seatingStatus.tableName}」。\n\n刪除 RSVP 將同時清理座位安排。\n\n確定要繼續嗎？`;
    }

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    try {
      setDeletingRSVPId(rsvpId);

      if (seatingStatus.hasSeating) {
        await clearGuestFromSeating(rsvpId);
      }

      await rsvpService.deleteRSVP(rsvpId, userId);
      await onRefresh();

      const successMessage = seatingStatus.hasSeating
        ? `已成功刪除 ${guestName} 的 RSVP 回覆並清理座位安排`
        : `已成功刪除 ${guestName} 的 RSVP 回覆`;
      alert(successMessage);

      onDataChange?.();

    } catch (error) {
      log.error('Error deleting RSVP:', error);
      alert(`刪除失敗：${error instanceof Error ? error.message : '請稍後再試'}`);
    } finally {
      setDeletingRSVPId(null);
    }
  }, [userId, checkGuestSeatingStatus, clearGuestFromSeating, onRefresh, onDataChange]);

  const handleSendIndividualNotification = useCallback(async (attendee: RSVPResponseWithTable) => {
    if (!attendee.guestEmail || !attendee.tableName || attendee.tableName === '未分配') {
      alert('賓客缺少 Email 或尚未分配座位，無法發送通知');
      return;
    }

    try {
      setSendingNotificationId(attendee.id);

      log.debug('發送通知給:', {
        guestName: attendee.guestName,
        guestEmail: attendee.guestEmail,
        tableName: attendee.tableName
      });

      await new Promise(resolve => setTimeout(resolve, 1000));

      alert(`已成功發送桌號通知給 ${attendee.guestName}`);

    } catch (error) {
      log.error('發送個別通知失敗:', error);
      alert(`發送通知失敗: ${error instanceof Error ? error.message : '請稍後再試'}`);
    } finally {
      setSendingNotificationId(null);
    }
  }, []);

  return {
    updatingGuestCount,
    editingNameId,
    editingNameValue,
    setEditingNameValue,
    updatingGuestName,
    deletingRSVPId,
    sendingNotificationId,
    checkGuestSeatingStatus,
    clearGuestFromSeating,
    handleUpdateGuestCount,
    handleGuestSideToggle,
    handleStartEditName,
    handleCancelEditName,
    handleUpdateGuestName,
    handleNameInputKeyDown,
    handleDeleteRSVP,
    handleSendIndividualNotification,
  };
}
